import { z } from "zod";
import { ISOString, LocalTimestampNoOffset, RoundtripTime, RoundtripTimeDate, TimezoneOffset } from "./DateTypes";
import { SpirometerID, SpirobankSmart2ID } from "./Types";
import { validate } from "uuid";

// The idea with branded types is we validate once in the handler, then for the rest of the lambda execution we know that an ID is a non-empty, valid uuid, string!
// We can also avoid accidentally passing a patientID to a function that expects an organizationID, for example (nominal typing). Branded types are the closest to nominal typing you can get in TS.
// This does not affect the usage of these as normal strings: for example, if you want, you can still call `toUpperCase` on a `PatientID`. You will lose the branding, but that's correct, because an upper cased `PatientID` is no longer a valid `PatientID`.
// This does not affect anything about the runtime behavior of these strings, it is purely a static construct.
// You don't need to change any existing functions, a `string & z.Brand<...>` is assignable to a `string`. You can incrementally change function signatures to more the more specific type as you go.
// This does not mean the frontend or mobile need to validate these IDs (unless you want to, of course), but for the dashboard, will provide the "nominal typing" benefit listed above.

export const trainingModeSuffix = "_training";
export type TrainingModeSuffix = typeof trainingModeSuffix;
const createTrainingModeUuidRefiner = <NormalSchema extends z.ZodSchema<string, z.ZodBrandedDef<typeof uuidSchema>, unknown>>(normalSchema: NormalSchema) => {
  type NormalType = z.infer<typeof normalSchema>;
  return (str: string): str is `${NormalType}${TrainingModeSuffix}` => {
    const [normalID, training, ...rest] = str.split("_");
    return normalSchema.safeParse(normalID).success && `_${training}` === trainingModeSuffix && rest.length === 0;
  };
};

/** For checking for any valid UUID */
export const uuidSchema = z.string().refine((str): str is `${string}-${string}-${string}-${string}-${string}` => validate(str));
export type UUID = z.infer<typeof uuidSchema>;

export const patientIDSchema = uuidSchema.brand('PatientID');
export type PatientID = z.infer<typeof patientIDSchema>;

export const demographicIDSchema = uuidSchema.brand('DemographicID');
export type DemographicID = z.infer<typeof demographicIDSchema>;

export const normalOrganizationIDSchema = uuidSchema.brand('NormalOrganizationID');
export type NormalOrganizationID = z.infer<typeof normalOrganizationIDSchema>;
export const trainingModeOrganizationIDSchema = z.string().refine(createTrainingModeUuidRefiner(normalOrganizationIDSchema));
export type TrainingModeOrganizationID = z.infer<typeof trainingModeOrganizationIDSchema>;
export const organizationIDSchema = z.union([normalOrganizationIDSchema, trainingModeOrganizationIDSchema]);
export type OrganizationID = z.infer<typeof organizationIDSchema>;

export const effortIDSchema = uuidSchema.brand('EffortID');
export type EffortID = z.infer<typeof effortIDSchema>;

export const seriesIDSchema = uuidSchema.brand('SeriesID');
export type SeriesID = z.infer<typeof seriesIDSchema>;

export const uploadGroupIDSchema = uuidSchema.brand('UploadGroupID');
export type UploadGroupID = z.infer<typeof uploadGroupIDSchema>;

export const sessionIDSchema = uuidSchema.brand('SessionID');
export type SessionID = z.infer<typeof sessionIDSchema>;

export const providerIDSchema = uuidSchema.brand('ProviderID');
export type ProviderID = z.infer<typeof providerIDSchema>;

export const incentiveSpirometryIDSchema = uuidSchema.brand('IncentiveSpirometryID');
export type IncentiveSpirometryID = z.infer<typeof incentiveSpirometryIDSchema>;

export const normalKioskIDSchema = uuidSchema.brand('NormalKioskID');
export type NormalKioskID = z.infer<typeof normalKioskIDSchema>;
export const trainingModeKioskIDSchema = z.string().refine(createTrainingModeUuidRefiner(normalKioskIDSchema));
export type TrainingModeKioskID = z.infer<typeof trainingModeKioskIDSchema>;
export const kioskIDSchema = z.union([normalKioskIDSchema, trainingModeKioskIDSchema]);
export type KioskID = z.infer<typeof kioskIDSchema>;

export const spirometerIDRegex = /^SM-005-Z\d\d\d\d\d\d$/;
export const spirometerIDSchema = z.custom<SpirometerID>(data => typeof data === 'string' && spirometerIDRegex.test(data));
export const spirobankSmart2IDSchema = z.custom<SpirobankSmart2ID>(data => typeof data === 'string' && /^spirobank_\d\d\d\d\d$/.test(data));

export const programIDSchema = z.string().min(1).transform(str => str.toLowerCase()).brand('ProgramID');
export type ProgramID = z.infer<typeof programIDSchema>;
/** Currently only used for studyIDs managed while in training mode. Note: without the unwrap {@link ProgramID} swallows {@link TrainingModeProgramIDForStudyIDs} for some reason... */
export const trainingModeProgramIDForStudyIDsSchema = programIDSchema.unwrap().refine((arg: string): arg is TrainingModeProgramIDForStudyIDs => arg.endsWith(trainingModeSuffix));
/** Currently only used for studyIDs managed while in training mode. */
export type TrainingModeProgramIDForStudyIDs = `${ProgramID}${TrainingModeSuffix}`;

export const maxStudyIDLength = 80;
/** A string type of min length 1 and max length {@link maxStudyIDLength} (inclusive). The max length requirement matches the validation performed by the Dashboard. */
export const studyIDSchema = z.string().min(1).max(maxStudyIDLength).brand('StudyID');
/** A string type of min length 1 and max length {@link maxStudyIDLength} (inclusive). The max length requirement matches the validation performed by the Dashboard. */
export type StudyID = z.infer<typeof studyIDSchema>;

export const emrObjectIDSchema = uuidSchema.brand('EmrObjectID');
export type EmrObjectID = z.infer<typeof emrObjectIDSchema>;
export const createSpecificEmrObjectIDSchema = (emrObjectID: UUID) => z.literal(emrObjectID).brand('EmrObjectID');

export const emrIDSchema = z.string().trim().min(1).brand('EmrID');
export type EmrID = z.infer<typeof emrIDSchema>;

/** For checking for any non-empty string */
export const nonEmptyStringSchema = z.string().min(1).brand('NonEmptyString');
export type NonEmptyString = z.infer<typeof nonEmptyStringSchema>;

/**
 * @param zodStringBase Optionally provide an instance of {@link z.ZodString} with refinements applied already
 * @example
 * ```ts
 * createStringEndsWithSchema("@zephyrx.com", z.string().email());
 * ```
 */
export const createStringEndsWithSchema = <T extends string>(endsWith: T, zodStringBase?: z.ZodString) => {
  const zodString = zodStringBase ?? z.string();
  return zodString.refine((str): str is `${string}${T}` => str.endsWith(endsWith), {
    message: `must end with "${endsWith}"`
  });
};

/** For checking any valid URL. Calls new URL() under the hood, so supports any URL that passes the native JS validation */
export const urlStrSchema = z.string().url().brand('URL');
export type URLStr = z.infer<typeof urlStrSchema>;

// For checking for a string or number meant to represent a unix timestamp is a valid timestamp
export const strInputTimestampSchema = z.string()
  .transform((str, ctx) => {
    const parsed = parseInt(str);
    if (isNaN(parsed) || (str !== `${parsed}`)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Not an integer: ${str}`
      });
      return z.NEVER;
    }
    return parsed;
  })
  .brand('timestamp');
export const numberInputTimestampSchema = z.number().int().brand('timestamp');
// intersect the inferred types to make the all variations will produce the same output type
export type Timestamp = z.infer<typeof strInputTimestampSchema> & z.infer<typeof numberInputTimestampSchema>;

export const roundtripTimeSchema = z.custom<RoundtripTime>(arg => {
  if (typeof arg !== 'string') {
    return false;
  }
  const timestampFromRtt = new Date(arg).getTime();
  if (isNaN(timestampFromRtt)) {
    return false;
  }
  return true;
}, arg => ({ code: z.ZodIssueCode.custom, message: `Not a valid date string: ${arg}` }));

export const roundtripTimeDateSchema = z.custom<RoundtripTimeDate>(arg => {
  return typeof arg === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(arg);
});

export const timezoneOffsetSchema = z.custom<TimezoneOffset>(arg => {
  return typeof arg === 'string' && /^[+-]\d{2}:\d{2}$/.test(arg);
});

export const localTimestampNoOffsetSchema = z.custom<LocalTimestampNoOffset>(arg => {
  return typeof arg === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(arg);
});

export const genericApiClientIDSchema = z.string().min(1).refine(val => !/^api_?.*/.test(val), { message: 'clientID should not start with api or api_. The prefix will be automatically prepended.' }).brand('GenericAPIClientID');
export type GenericAPIClientID = z.infer<typeof genericApiClientIDSchema>;

export const isoStringSchema = z
  .string()
  // Milliseconds quantifier due to mobile bug that treats isoStrings like decimals 
  // and strips the last zeros - will not be able to fix until the next mobile release.
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{0,3}Z$/)
  .transform(arg => {
    const needsFixing = arg.length < 24;
    if (!needsFixing) {
      return arg as ISOString;
    }

    // Has 0, 1, or 2 millisecond digits, e.g. "Z", "0Z", "00Z"
    const partToFix = arg.slice(20);
    // Chop off the Z, add missing 0s, then add Z back
    const fixedPart = `${partToFix.slice(0, -1).padEnd(3, '0')}Z`;
    return arg.replace(partToFix, fixedPart) as ISOString;
  });

export const positiveNumberSchema = z.number().min(1).brand("Positive");
export type PositiveNumber = z.infer<typeof positiveNumberSchema>;

/** 
 * Niox Patient imposes an _application layer_ uniqueness check on `PatientID`s but only when creating patients. Still, we should
 * probably only use a ZEPHYRx identifier that is also also unique, such as a StudyID.
 * 
 * The DB schema type is `nvarchar(90)` though, so we need to check its length is less than or equal to 90. Even if this wasn't the case,
 * it's probably wise to do so to avoid a 1 GB string patient name from someone trying to do something malicious lol.
 * 
 * IMPORTANT: If changing schema, @see isNioxPatientID in v1 libs/feno-lib.js also!!!
 */
export const nioxPatientIDSchema = z.string().trim().min(1).max(90).brand("NioxPatientID");
export type NioxPatientID = z.infer<typeof nioxPatientIDSchema>;

export const fenoDeviceSerialNumberLength = 9;
export const fenoDeviceSerialNumberRegex = new RegExp(`^\\d{${fenoDeviceSerialNumberLength}}$`);
export const fenoDeviceSerialNumberSchema = z
  .string()
  .trim()
  .refine((s): s is `${number}${number}${number}${number}${number}${number}${number}${number}${number}` => fenoDeviceSerialNumberRegex.test(s));
export type FenoDeviceSerialNumber = z.infer<typeof fenoDeviceSerialNumberSchema>;

export const noBreathFenoDeviceSerialNumberRegex = /^NN\d{6}$/;
export const noBreathFenoDeviceSerialNumberSchema = z
  .string()
  .trim()
  .refine((s): s is `NN${number}${number}${number}${number}${number}${number}` => noBreathFenoDeviceSerialNumberRegex.test(s));
export type NoBreathFenoDeviceSerialNumber = z.infer<typeof noBreathFenoDeviceSerialNumberSchema>;

export const PatientCodeRegex = /^\d{8}$/; // Changing to numbers only, keeping separate incase we'd like to update in future.
export const PatientCodeSchema = z
  .string()
  .trim()
  .refine((s): s is `${number}${number}${number}${number}${number}${number}${number}${number}` => PatientCodeRegex.test(s));
export type PatientOneTimeCode = z.infer<typeof PatientCodeSchema>;

export const ProviderCodeRegex = /^\d{8}$/;
export const ProviderCodeSchema = z
  .string()
  .trim()
  .refine((s): s is `${number}${number}${number}${number}${number}${number}${number}${number}` => ProviderCodeRegex.test(s));
export type ProviderOneTimeCode = z.infer<(typeof ProviderCodeSchema)>;

// eslint-disable-next-line @typescript-eslint/ban-types
export type NonSwallowingString = string & {};
